<template>
  <div class="app-page promotion-page" v-loading="loading">
    <div class="box-card-header">
      <div class="title">{{ $t('promotionCenter') }}</div>
    </div>

    <div class="box-card-content">
      <div class="panel identity-panel">
        <div class="identity-line">
          <span class="label">{{ ui.currentLevel }}：</span>
          <b>{{ currentPromotionLevel }}</b>
          <div class="head-links">
            <span class="detail-link" @click="openInvitationDetail">{{ ui.invitationDetails }}</span>
            <span class="detail-link" @click="openCommissionDetail('trade')">{{ ui.commissionDetails }}</span>
          </div>
        </div>

        <div class="summary-grid">
          <div
            v-for="item in commissionCards"
            :key="item.key"
            class="summary-card"
            @click="openCommissionDetail(item.key)"
          >
            <strong>{{ item.total }}</strong><span>MEQ</span>
            <div>{{ item.label }}</div>
          </div>
        </div>

        <div class="today-grid">
          <div v-for="item in commissionCards" :key="'today-' + item.key" class="today-item">
            <span>{{ item.todayLabel }}</span><b>{{ item.today }}</b>
          </div>
        </div>
      </div>

      <div v-if="!isMaster && next.next_level" class="panel upgrade-panel">
        <div class="upgrade-head">
          <h3>{{ ui.nextLevel }}：{{ next.next_level_name }}</h3>
          <span class="direct-badge">{{ ui.directInvite }} {{ stats.direct_count || 0 }}</span>
        </div>

        <div class="upgrade-condition-card">
          <div class="condition-label">{{ ui.upgradeCondition }}</div>
          <div class="condition-title">{{ conditionHeadline }}</div>

          <div v-if="Number(next.self_valid_required) === 1" class="self-valid-row">
            <span>{{ ui.selfValidCondition }}</span>
            <b :class="Number(next.self_valid) === 1 ? 'done' : 'pending'">
              {{ Number(next.self_valid) === 1 ? ui.completed : ui.notCompleted }}
            </b>
          </div>

          <div class="condition-count-row">
            <span>{{ requirementText(next.requirement_type) }}</span>
            <b>{{ next.current_count || 0 }} / {{ next.required_count || 0 }}</b>
          </div>
          <div class="progress-track">
            <div class="progress-value" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <div class="progress-footer">
            <span>{{ ui.progress }} {{ progressPercent }}%</span>
            <span>{{ ui.remaining }} {{ next.remaining_count || 0 }}</span>
          </div>
        </div>

        <div class="rates-compare-grid">
          <div v-for="item in rateCompareRows" :key="item.key" class="rate-compare-card">
            <span class="rate-label">{{ item.label }}</span>
            <div class="rate-flow">
              <div class="rate-box">
                <b>{{ item.current }}%</b>
                <small>{{ ui.current }}</small>
              </div>
              <span class="arrow">→</span>
              <div class="rate-box next">
                <b>{{ item.next }}%</b>
                <small>{{ next.next_level_name }}</small>
              </div>
            </div>
          </div>
        </div>
        <div class="rule-note">{{ ui.ruleNote }}</div>
      </div>

      <div v-else-if="isMaster" class="panel master-panel">
        <div class="panel-head">
          <h3>{{ ui.masterRates }}</h3>
          <span class="detail-link" @click="openInvitationDetail">{{ ui.invitationDetails }}</span>
        </div>
        <div class="master-rates">
          <div v-for="item in currentRateRows" :key="item.key">
            <b>{{ item.value }}%</b><span>{{ item.label }}</span>
          </div>
        </div>
      </div>

      <div class="panel invite-panel">
        <div class="invite-main">
          <div class="field-title">{{ ui.inviteCode }}</div>
          <div class="code-line">
            <b>{{ info.invite_code || '--' }}</b>
            <button class="kg-btn2" @click="copy(info.invite_code)">{{ ui.copy }}</button>
          </div>
          <div class="field-title mt">{{ ui.inviteLink }}</div>
          <div class="link-line">{{ inviteUrl || '--' }}</div>
          <div class="button-row three-buttons">
            <button class="kg-btn2" @click="copy(inviteUrl)">{{ ui.copyLink }}</button>
            <button class="kg-btn3" @click="openPromotionMaterials">{{ ui.promotionMaterials }}</button>
            <button class="kg-btn3" @click="openInvitationDetail">{{ ui.invitationDetails }}</button>
          </div>
        </div>
        <div class="qr-wrap" v-if="inviteUrl">
          <vue-qr :text="inviteUrl" :size="118" :margin="0" />
          <span>{{ ui.scanRegister }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import VueQr from 'vue-qr'

const H5_ORIGIN = 'https://h5.heymetaplayer.com'

export default {
  name: 'PromotionCenter',
  components: { VueQr },
  data () {
    return { loading: false, info: {}, stats: {}, next: {} }
  },
  computed: {
    ...mapState(['userInfo']),
    isMaster () { return Number(this.info.is_master_agent) === 1 || Number(this.userInfo.identity) === 1 },
    currentPromotionLevel () { return this.info.promotion_level_name || this.ui.normalUser },
    inviteUrl () {
      if (!this.info.invite_code || Number(this.info.invite_enabled) !== 1) return ''
      return `${window.location.protocol}//${window.location.host}/sign/sign-up?invite_code=${this.info.invite_code}`
    },
    promotionMaterialUrl () {
      if (!this.info.invite_code) return ''
      return `${H5_ORIGIN}/static/promotion/user.html?invite_code=${encodeURIComponent(this.info.invite_code)}`
    },
    tradeRates () { return this.info.trade_rates || {} },
    interestRates () { return this.info.interest_rates || {} },
    memberRates () { return this.info.member_rates || {} },
    riskRates () { return this.info.risk_rates || {} },
    summary () { return this.info.commission_summary || {} },
    commissionCards () {
      return [
        { key: 'trade', label: this.ui.tradeTotal, todayLabel: this.ui.tradeToday, total: this.summaryValue('trade', 'total'), today: this.summaryValue('trade', 'today') },
        { key: 'interest', label: this.ui.interestTotal, todayLabel: this.ui.interestToday, total: this.summaryValue('interest', 'total'), today: this.summaryValue('interest', 'today') },
        { key: 'member', label: this.ui.memberTotal, todayLabel: this.ui.memberToday, total: this.summaryValue('member', 'total'), today: this.summaryValue('member', 'today') },
        { key: 'risk_gift', label: this.ui.riskTotal, todayLabel: this.ui.riskToday, total: this.summaryValue('risk_gift', 'total'), today: this.summaryValue('risk_gift', 'today') }
      ]
    },
    currentRateKey () {
      if (this.isMaster) return 'master'
      const level = Number(this.info.promotion_level || 0)
      return level === 1 ? 'level_1' : level === 2 ? 'level_2' : level === 3 ? 'level_3' : 'normal'
    },
    nextRateKey () {
      const level = Number(this.next.next_level || 0)
      return level === 1 ? 'level_1' : level === 2 ? 'level_2' : level === 3 ? 'level_3' : this.currentRateKey
    },
    rateCompareRows () {
      return [
        { key: 'trade', label: this.ui.tradeCommission, current: this.rate(this.tradeRates, this.currentRateKey), next: this.rate(this.tradeRates, this.nextRateKey) },
        { key: 'interest', label: this.ui.interestCommission, current: this.rate(this.interestRates, this.currentRateKey), next: this.rate(this.interestRates, this.nextRateKey) },
        { key: 'member', label: this.ui.memberCommission, current: this.rate(this.memberRates, this.currentRateKey), next: this.rate(this.memberRates, this.nextRateKey) },
        { key: 'risk_gift', label: this.ui.riskCommission, current: this.rate(this.riskRates, this.currentRateKey), next: this.rate(this.riskRates, this.nextRateKey) }
      ]
    },
    currentRateRows () {
      return [
        { key: 'trade', label: this.ui.tradeCommission, value: this.rate(this.tradeRates, 'master') },
        { key: 'interest', label: this.ui.interestCommission, value: this.rate(this.interestRates, 'master') },
        { key: 'member', label: this.ui.memberCommission, value: this.rate(this.memberRates, 'master') },
        { key: 'risk', label: this.ui.riskCommission, value: this.rate(this.riskRates, 'master') }
      ]
    },
    progressPercent () {
      const required = Number(this.next.required_count || 0)
      const current = Number(this.next.current_count || 0)
      if (required <= 0) return Number(this.next.condition_completed) === 1 ? 100 : 0
      const countProgress = Math.min(100, Math.round((current / required) * 100))
      if (Number(this.next.self_valid_required) === 1 && Number(this.next.self_valid) !== 1) {
        return Math.min(99, countProgress)
      }
      return countProgress
    },
    conditionHeadline () {
      const countText = `${this.requirementText(this.next.requirement_type)}${this.ui.reach}${this.next.required_count || 0}${this.ui.personUnit}`
      if (Number(this.next.self_valid_required) === 1) {
        return `${this.ui.selfMustValid} + ${countText}`
      }
      return countText
    },
    ui () {
      const l = String(this.$i18n.locale || '').toLowerCase()
      if (l.indexOf('ja') === 0) {
        return {
          currentLevel: '現在のレベル',
          normalUser: '一般ユーザー',
          commissionDetails: 'コミッション明細',
          invitationDetails: '紹介明細',
          nextLevel: '次のレベル',
          directInvite: '直接紹介',
          current: '現在',
          required: '必要数',
          remaining: '残り',
          masterRates: 'マスター代理コミッション率',
          inviteCode: '紹介コード',
          inviteLink: '紹介リンク',
          copy: 'コピー',
          copyLink: '紹介リンクをコピー',
          scanRegister: 'スキャンして登録',
          promotionMaterials: 'プロモーション資料',
          tradeTotal: '取引総コミッション',
          interestTotal: '利息総コミッション',
          memberTotal: '会員総コミッション',
          riskTotal: 'リスク総コミッション',
          tradeToday: '本日の取引コミッション',
          interestToday: '本日の利息コミッション',
          memberToday: '本日の会員コミッション',
          riskToday: '本日のリスクコミッション',
          tradeCommission: '取引コミッション',
          interestCommission: '利息コミッション',
          memberCommission: '会員コミッション',
          riskCommission: 'リスク補償コミッション',
          upgradeCondition: '昇格条件',
          selfValidCondition: '本人が有効ユーザー',
          completed: '達成済み',
          notCompleted: '未達成',
          progress: '進捗',
          reach: 'が',
          personUnit: '名に到達',
          selfMustValid: '本人が有効ユーザーになる',
          ruleNote: '表示率は各レベルの累積上限率です。間接取引は実際の紹介関係と差額率で計算され、本人の取引・利息には本人の紹介コミッションは発生しません。'
        }
      }
      if (l.indexOf('en') === 0) {
        return {
          currentLevel: 'Current Level',
          normalUser: 'Normal User',
          commissionDetails: 'Commission Details',
          invitationDetails: 'Invitation Details',
          nextLevel: 'Next Level',
          directInvite: 'Direct Invites',
          current: 'Current',
          required: 'Required',
          remaining: 'Remaining',
          masterRates: 'Master Commission Rates',
          inviteCode: 'Invitation Code',
          inviteLink: 'Invitation Link',
          copy: 'Copy',
          copyLink: 'Copy Invitation Link',
          scanRegister: 'Scan to register',
          promotionMaterials: 'Promotion Materials',
          tradeTotal: 'Trade Commission',
          interestTotal: 'Interest Commission',
          memberTotal: 'Member Commission',
          riskTotal: 'Risk Gift Commission',
          tradeToday: 'Trade Today',
          interestToday: 'Interest Today',
          memberToday: 'Member Today',
          riskToday: 'Risk Gift Today',
          tradeCommission: 'Trade Commission',
          interestCommission: 'Interest Commission',
          memberCommission: 'Member Commission',
          riskCommission: 'Risk Gift Commission',
          upgradeCondition: 'Upgrade Requirement',
          selfValidCondition: 'Your account is a valid user',
          completed: 'Completed',
          notCompleted: 'Not completed',
          progress: 'Progress',
          reach: ' reaches ',
          personUnit: '',
          selfMustValid: 'Become a valid user yourself',
          ruleNote: 'Rates shown are cumulative maximum rates. Indirect business is settled by the real referral chain and differential rates. Users do not receive referral commission from their own trading or interest.'
        }
      }
      return {
        currentLevel: '您当前为',
        normalUser: '普通用户',
        commissionDetails: '佣金明细',
        invitationDetails: '邀请明细',
        nextLevel: '下一等级',
        directInvite: '直接邀请',
        current: '当前',
        required: '升级要求',
        remaining: '还差',
        masterRates: '主代理返佣比例',
        inviteCode: '我的邀请码',
        inviteLink: '邀请链接',
        copy: '复制',
        copyLink: '复制邀请链接',
        scanRegister: '扫码注册',
        promotionMaterials: '推广资料',
        tradeTotal: '交易总佣金',
        interestTotal: '利息总佣金',
        memberTotal: '会员总佣金',
        riskTotal: '风险总佣金',
        tradeToday: '今日交易返佣',
        interestToday: '今日利息佣金',
        memberToday: '今日会员佣金',
        riskToday: '今日风险赠金佣金',
        tradeCommission: '交易佣金',
        interestCommission: '利息佣金',
        memberCommission: '会员佣金',
        riskCommission: '风险补偿佣金',
        upgradeCondition: '升级条件',
        selfValidCondition: '本人有效用户状态',
        completed: '已完成',
        notCompleted: '未完成',
        progress: '完成进度',
        reach: '达到',
        personUnit: '个',
        selfMustValid: '本人先成为有效用户',
        ruleNote: '页面展示的比例为该等级累计最高返佣比例；非直属业务按照真实推荐关系及层级差额计算实际佣金。本人产生的交易及利息不获得自己的推广返佣。'
      }
    }
  },
  created () { this.loadInfo() },
  methods: {
    loadInfo () {
      this.loading = true
      this.$http.get('/api/promotion/info').then(result => {
        this.info = result || {}
        this.stats = this.info.stats || {}
        this.next = this.info.next || {}
      }).finally(() => { this.loading = false })
    },
    summaryValue (key, field) { return this.summary[key] && this.summary[key][field] !== undefined ? this.summary[key][field] : '0.00' },
    rate (rates, key) { return rates && rates[key] !== undefined && rates[key] !== null && rates[key] !== '' ? rates[key] : 0 },
    requirementText (type) {
      const map = {
        direct_valid_user: this.$t('promotionRequirementDirectValid'),
        direct_level3_plus: this.$t('promotionRequirementDirectLevel3Plus'),
        direct_level2_plus: this.$t('promotionRequirementDirectLevel2Plus')
      }
      return map[type] || this.$t('promotionUpgradeRequirement')
    },
    openInvitationDetail () { this.$router.push({ name: this.isMaster ? 'proxyInvitationDetail' : 'userInvitationDetail' }) },
    openCommissionDetail (type) { this.$router.push({ name: this.isMaster ? 'proxyCommissionDetailsNew' : 'userCommissionDetailsNew', query: { type: type || 'trade' } }) },
    openPromotionMaterials () {
      if (!this.promotionMaterialUrl) {
        this.$message.warning(this.ui.noPromotionLink || this.ui.inviteCode)
        return
      }
      window.open(this.promotionMaterialUrl, '_blank', 'noopener')
    },
    copy (text) {
      if (!text) return
      this.$copyText(text).then(() => this.$message.success(this.$t('promotionCopySuccess'))).catch(() => this.$message.error(this.$t('promotionCopyFailed')))
    }
  }
}
</script>

<style scoped lang="scss">
.promotion-page {
  min-height: calc(100vh - 90px);
  margin: 0 -38px;
  padding: 0 38px 38px;
  color: #d7edf4;
  background: radial-gradient(circle at 75% 10%, rgba(0, 194, 242, .08), transparent 30%), linear-gradient(180deg, rgba(2, 13, 20, .25), rgba(2, 13, 20, .02));
  .box-card-header { padding-top: 18px; margin-bottom: 14px; .title { color: #00c8f4; font-size: 18px; font-weight: 600; } }
}
.panel { border: 1px solid rgba(31, 146, 176, .35); background: linear-gradient(180deg, rgba(8, 33, 45, .88), rgba(4, 20, 29, .92)); border-radius: 7px; padding: 18px; margin-bottom: 16px; }
.identity-line, .panel-head, .upgrade-head { display: flex; align-items: center; gap: 10px; }
.identity-line .label { color: #7993a3; }
.identity-line b { font-size: 16px; color: #fff; flex: 1; }
.head-links { display: flex; gap: 18px; }
.detail-link { color: #00c8f4; cursor: pointer; font-size: 13px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 18px; }
.summary-card { border: 1px solid #31546a; background: #0c2938; text-align: center; padding: 15px 6px; cursor: pointer; }
.summary-card strong { font-size: 20px; color: #fff; }
.summary-card > span { font-size: 10px; margin-left: 4px; }
.summary-card div { font-size: 12px; color: #9db0bd; margin-top: 8px; }
.today-grid { margin-top: 14px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 0 24px; }
.today-item { display: flex; justify-content: space-between; border-bottom: 1px solid rgba(92, 126, 145, .35); padding: 8px 0; font-size: 12px; }
.upgrade-head { justify-content: space-between; }
.upgrade-head h3 { margin: 0; color: #fff; font-size: 18px; }
.direct-badge { border: 1px solid #00bce9; color: #00d2ff; padding: 7px 15px; border-radius: 18px; font-size: 12px; }
.upgrade-condition-card { margin-top: 14px; border: 1px solid #35566b; border-radius: 7px; padding: 18px; background: rgba(5, 27, 40, .58); }
.condition-label { color: #748f9f; font-size: 12px; }
.condition-title { margin-top: 9px; color: #fff; font-weight: 700; font-size: 16px; }
.self-valid-row, .condition-count-row, .progress-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 14px; font-size: 12px; }
.self-valid-row { color: #94a9b6; border-top: 1px solid rgba(69, 101, 120, .35); padding-top: 12px; }
.self-valid-row .done { color: #34d69c; }
.self-valid-row .pending { color: #ffbf55; }
.condition-count-row span { color: #9bb1be; }
.condition-count-row b { color: #fff; font-size: 14px; }
.progress-track { height: 8px; margin-top: 9px; border-radius: 999px; background: #0d2735; overflow: hidden; }
.progress-value { height: 100%; border-radius: inherit; background: linear-gradient(90deg, #00aee7, #00d8ff); transition: width .3s ease; }
.progress-footer { margin-top: 8px; color: #6f8998; }
.rates-compare-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 26px; margin-top: 18px; border-top: 1px solid rgba(66, 104, 128, .4); padding-top: 14px; }
.rate-compare-card { display: grid; grid-template-columns: 92px 1fr; align-items: center; gap: 10px; }
.rate-label { color: #c7d5de; }
.rate-flow { display: grid; grid-template-columns: minmax(90px, 1fr) 30px minmax(90px, 1fr); align-items: center; gap: 8px; }
.rate-box { border: 1px solid #476477; text-align: center; padding: 7px; }
.rate-box b { display: block; color: #fff; font-size: 17px; }
.rate-box small { color: #738a99; }
.rate-box.next { border-color: #00c8f4; }
.arrow { text-align: center; color: #78909e; font-size: 20px; }
.rule-note { font-size: 11px; color: #708795; line-height: 1.7; margin-top: 13px; }
.panel-head { justify-content: space-between; }
.panel-head h3 { margin: 0; color: #fff; font-size: 16px; }
.master-rates { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 16px; }
.master-rates > div { border: 1px solid #476477; text-align: center; padding: 13px; }
.master-rates b { display: block; font-size: 20px; color: #fff; }
.master-rates span { font-size: 11px; color: #89a0ae; }
.invite-panel { display: flex; gap: 28px; }
.invite-main { flex: 1; }
.field-title { font-size: 12px; color: #728b9b; }
.field-title.mt { margin-top: 14px; }
.code-line { display: flex; align-items: center; gap: 15px; margin-top: 7px; }
.code-line b { font-size: 22px; color: #fff; letter-spacing: 2px; }
.link-line { color: #b7c7d0; font-size: 12px; margin-top: 6px; word-break: break-all; }
.button-row { display: flex; gap: 12px; margin-top: 13px; }
.three-buttons button { min-width: 120px; }
.qr-wrap { width: 130px; text-align: center; }
.qr-wrap span { display: block; margin-top: 6px; color: #8197a5; font-size: 11px; }
.kg-btn3 { border: 1px solid #315f76; background: transparent; color: #a9c1cf; padding: 7px 16px; cursor: pointer; }

@media (max-width: 1100px) {
  .rates-compare-grid { grid-template-columns: 1fr; }
}
</style>
